<template>
  <panel-layout
    id="brand-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="brand" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ brand.isNewRecord ? $t('brand.new') : brand.name }}
          </h2>
          <div v-if="!brand.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.brand.edit.id', { id: brand.idProductBrand })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="brand" ref="form" class="h-full w-full">
      <div class="w-full px-6 py-4">
        <div class="flex space-x-4 mb-6">
          <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')" :info="$t('pages.brand.edit.name')">
            <text-field v-model="brand.name" :label="$t('general.title')" :status="status" />
          </o-field-group>
        </div>
      </div>
    </o-form>

    <template #footer>
      <div v-if="brand">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import { TextField, TextAreaField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const props = defineProps<{ id?: string }>()

const panel = usePanel()
const confirmDialog = ref<any>(null)
const form = ref<any>()

const brand = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(brand)

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      brand.value = (await $api.productBrand.defaults({} as any)).data
    } else {
      brand.value = (await $api.productBrand.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

async function save (close = false) {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _brand = (await $api.productBrand.save(brand.value)).data

    if (_brand) {
      brand.value = _brand
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.brand.edit.success'),
        text: $i18n.t('pages.brand.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ProductBrand', _brand)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

function cancel (force = false) {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

defineExpose({ save, cancel, unsavedChanges })
</script>
